import { getBudgetExchangeRates } from '@/lib/budgetExchangeRates';
import { apiFetch } from '@/lib/api';

// ===============================
// Type Definitions
// ===============================

/**
 * Budget settings stored at proposal level
 * NOTE: Currency is NOT stored here — the single source of truth is
 * `client_events.base_currency`, exposed as `ProposalBudgetFull.base_currency`.
 */
export interface BudgetSettings {
  contingency_percent: number;
  contingency_fixed: number | null;
  planning_overhead_percent: number;
  planning_overhead_fixed: number | null;
  profit_margin_percent: number;
  profit_margin_fixed: number | null;
  budget_note: string | null;
  /** Show budget figures on the client proposal (default true). */
  show_on_proposal?: boolean;
}

/**
 * Room breakdown for a venue
 */
export interface VenueRoomBreakdown {
  single_rooms: number;
  single_room_rate: number | null;
  double_rooms: number;
  double_room_rate: number | null;
  room_nights: number | null;
}

/**
 * Activities breakdown for a venue
 */
export interface VenueActivitiesBreakdown {
  activities_number: number | null;
  activities_cost_per: number | null; // cents per activity
  activities_currency: string;
}

/**
 * Meals breakdown for a venue
 */
export interface VenueMealsBreakdown {
  breakfast_cost_per_attendee: number | null; // cents per breakfast per attendee
  breakfast_count: number;
  lunch_cost_per_attendee: number | null; // cents per lunch per attendee
  lunch_count: number;
  dinner_cost_per_attendee: number | null; // cents per dinner per attendee
  dinner_count: number;
  alcohol_cost_per_attendee: number | null; // cents per alcohol per attendee
  alcohol_count: number;
  meals_currency: string;
}

/**
 * Meetings breakdown for a venue
 */
export interface VenueMeetingsBreakdown {
  meeting_room_rental: number | null; // cents per meeting
  meeting_setup_fees: number | null; // cents per meeting
  meeting_av_cost: number | null; // cents per meeting
  number_of_meetings: number;
  coffee_cost_per_attendee: number | null; // cents per coffee per attendee
  coffee_count: number;
  meetings_currency: string;
}

/**
 * Single miscellaneous cost item
 */
export interface MiscellaneousCostItem {
  id: string;
  title: string;
  amount: number; // cents
  type: 'per_trip' | 'per_person';
  sort_order: number;
}

/**
 * Venue-level other costs
 */
export interface VenueOtherCosts {
  venue_currency: string;
  flight_cost_per_attendee: number | null; // cents
  ground_transport_cost: number | null; // cents
  miscellaneous_costs: MiscellaneousCostItem[] | null;
  budget_note: string | null;
}

/**
 * Line item in budget breakdown
 */
export interface BudgetLineItem {
  category: string;
  label: string;
  per_attendee: number | null;
  subtotal: number;
  details?: string;
}

/**
 * Computed budget for a single venue
 */
export interface VenueBudgetComputed {
  venue_id: string;
  venue_name: string;
  shortlist_id: string;
  destination_city: string;
  destination_country: string;

  accommodation: BudgetLineItem;
  flight_cost: BudgetLineItem;
  activities: BudgetLineItem;
  ground_transport: BudgetLineItem;
  meals: BudgetLineItem;
  meetings: BudgetLineItem;
  miscellaneous: BudgetLineItem;

  subtotal: number;

  contingency: {
    percent: number | null;
    fixed: number | null;
    amount: number;
  };
  planning_overhead: {
    percent: number | null;
    fixed: number | null;
    amount: number;
  };
  profit_margin: {
    percent: number | null;
    fixed: number | null;
    amount: number;
  };

  total_program_cost: number;
  per_attendee_cost: number;

  // Currency info
  venue_currency: string; // Currency for editing venue costs

  // Raw venue costs for editing (in venue currency, cents)
  venue_flight_cost_per_attendee: number | null;
  venue_ground_transport_cost: number | null;
  venue_miscellaneous_costs: MiscellaneousCostItem[] | null;

  // Per-venue budget note
  budget_note: string | null;
}

/**
 * Full budget for a proposal
 */
export interface ProposalBudgetFull {
  event_id: string;
  event_name: string;
  attendees: number;
  nights: number;
  base_currency: string | null;
  settings: BudgetSettings;
  venues: VenueBudgetComputed[];
  summary: Array<{
    shortlist_id: string;
    venue_id: string;
    venue_name: string;
    per_attendee: number;
    total_cost: number;
  }>;
}

/**
 * Exchange rates
 */
export interface ExchangeRates {
  base: string;
  rates: Record<string, number>;
  timestamp: number;
}

/**
 * Input for updating budget settings
 */
export interface UpdateBudgetSettingsInput {
  contingency_percent?: number;
  contingency_fixed?: number | null;
  planning_overhead_percent?: number;
  planning_overhead_fixed?: number | null;
  profit_margin_percent?: number;
  profit_margin_fixed?: number | null;
  budget_note?: string | null;
}

/**
 * Input for updating room breakdown
 */
export interface UpdateRoomBreakdownInput {
  single_rooms?: number;
  single_room_rate?: number | null;
  double_rooms?: number;
  double_room_rate?: number | null;
  room_nights?: number | null;
}

// ===============================
// Helper
// ===============================

async function parseResponse<T>(res: Response, errorMsg: string): Promise<T> {
  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}));
    const error = new Error(errorData.error?.message || errorData.message || errorMsg);
    (error as any).status = res.status;
    (error as any).response = errorData;
    throw error;
  }
  const json = await res.json();
  return json.data ?? json;
}

// ===============================
// Budget Service
// ===============================

export class BudgetService {
  /**
   * Get budget settings for a proposal
   */
  async getBudgetSettings(eventId: string): Promise<BudgetSettings> {
    const res = await apiFetch(`/api/budget/settings/${eventId}`);
    const data = await parseResponse<{ settings: BudgetSettings }>(res, 'Failed to get budget settings');
    return data.settings;
  }

  /**
   * Update budget settings for a proposal
   */
  async updateBudgetSettings(
    eventId: string,
    input: UpdateBudgetSettingsInput
  ): Promise<string> {
    const res = await apiFetch(
      `/api/budget/settings/${eventId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    const data = await parseResponse<{ id: string }>(res, 'Failed to update budget settings');
    return data.id;
  }

  /**
   * Get room breakdown for a venue
   */
  async getVenueRoomBreakdown(shortlistId: string): Promise<VenueRoomBreakdown | null> {
    const res = await apiFetch(`/api/budget/room-breakdown/${shortlistId}`);
    const data = await parseResponse<{ breakdown: VenueRoomBreakdown | null }>(res, 'Failed to get room breakdown');
    return data.breakdown;
  }

  /**
   * Update room breakdown for a venue
   */
  async updateVenueRoomBreakdown(
    shortlistId: string,
    input: UpdateRoomBreakdownInput
  ): Promise<void> {
    const res = await apiFetch(
      `/api/budget/room-breakdown/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    await parseResponse<void>(res, 'Failed to update room breakdown');
  }

  /**
   * Get activities breakdown for a venue
   */
  async getVenueActivitiesBreakdown(shortlistId: string) {
    const res = await apiFetch(`/api/budget/activities-breakdown/${shortlistId}`);
    const data = await parseResponse<{ breakdown: VenueActivitiesBreakdown }>(res, 'Failed to get activities breakdown');
    return data.breakdown;
  }

  /**
   * Update activities breakdown for a venue
   */
  async updateVenueActivitiesBreakdown(shortlistId: string, input: Partial<VenueActivitiesBreakdown>) {
    const res = await apiFetch(
      `/api/budget/activities-breakdown/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    await parseResponse<void>(res, 'Failed to update activities breakdown');
  }

  /**
   * Get meals breakdown for a venue
   */
  async getVenueMealsBreakdown(shortlistId: string) {
    const res = await apiFetch(`/api/budget/meals-breakdown/${shortlistId}`);
    const data = await parseResponse<{ breakdown: VenueMealsBreakdown }>(res, 'Failed to get meals breakdown');
    return data.breakdown;
  }

  /**
   * Update meals breakdown for a venue
   */
  async updateVenueMealsBreakdown(shortlistId: string, input: Partial<VenueMealsBreakdown>) {
    const res = await apiFetch(
      `/api/budget/meals-breakdown/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    await parseResponse<void>(res, 'Failed to update meals breakdown');
  }

  /**
   * Get meetings breakdown for a venue
   */
  async getVenueMeetingsBreakdown(shortlistId: string) {
    const res = await apiFetch(`/api/budget/meetings-breakdown/${shortlistId}`);
    const data = await parseResponse<{ breakdown: VenueMeetingsBreakdown }>(res, 'Failed to get meetings breakdown');
    return data.breakdown;
  }

  /**
   * Update meetings breakdown for a venue
   */
  async updateVenueMeetingsBreakdown(shortlistId: string, input: Partial<VenueMeetingsBreakdown>) {
    const res = await apiFetch(
      `/api/budget/meetings-breakdown/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    await parseResponse<void>(res, 'Failed to update meetings breakdown');
  }

  /**
   * Get venue other costs (currency, flight, ground transport, miscellaneous)
   */
  async getVenueOtherCosts(shortlistId: string): Promise<VenueOtherCosts | null> {
    const res = await apiFetch(`/api/budget/venue-other-costs/${shortlistId}`);
    const data = await parseResponse<{ costs: VenueOtherCosts | null }>(res, 'Failed to get venue other costs');
    return data.costs;
  }

  /**
   * Update venue currency
   */
  async updateVenueCurrency(shortlistId: string, venueCurrency: string): Promise<void> {
    const res = await apiFetch(
      `/api/budget/venue-currency/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify({ venue_currency: venueCurrency }),
      }
    );
    await parseResponse<void>(res, 'Failed to update venue currency');
  }

  /**
   * Update venue other costs (flight, ground transport, miscellaneous)
   */
  async updateVenueOtherCosts(
    shortlistId: string,
    input: {
      flight_cost_per_attendee?: number | null;
      ground_transport_cost?: number | null;
      miscellaneous_costs?: MiscellaneousCostItem[] | null;
      budget_note?: string | null;
    }
  ): Promise<void> {
    const res = await apiFetch(
      `/api/budget/venue-other-costs/${shortlistId}`,
      {
        method: 'PUT',
        body: JSON.stringify(input),
      }
    );
    await parseResponse<void>(res, 'Failed to update venue other costs');
  }

  /**
   * Get full computed budget for a proposal
   */
  async getFullBudget(eventId: string): Promise<ProposalBudgetFull> {
    const res = await apiFetch(`/api/budget/full/${eventId}`);
    const data = await parseResponse<{ budget: ProposalBudgetFull }>(res, 'Failed to get full budget');
    return data.budget;
  }

  /**
   * Get exchange rates
   */
  async getExchangeRates(): Promise<ExchangeRates> {
    const rates = await getBudgetExchangeRates();
    return { ...rates, timestamp: rates.timestamp ?? 0 };
  }
}
